import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import type { RuntimeConfig } from '../types/api';

// Amplify stores OAuth PKCE state in localStorage. If a user has stale keys
// from a different origin (e.g. localhost), Cognito rejects the redirect_uri.
// Clear any keys that reference a different origin on startup.
function clearStaleOAuthState() {
  const currentOrigin = window.location.origin;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (!key.startsWith('CognitoIdentityServiceProvider.') && !key.startsWith('amplify-')) continue;
    const val = localStorage.getItem(key) ?? '';
    if (val.startsWith('http') && !val.startsWith(currentOrigin)) {
      localStorage.removeItem(key);
    }
  }
}

export function configureAmplify(config: RuntimeConfig) {
  clearStaleOAuthState();
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: {
          oauth: {
            domain: config.userPoolDomain,
            scopes: ['email', 'openid', 'profile'],
            redirectSignIn: [window.location.origin],
            redirectSignOut: [window.location.origin],
            responseType: 'code',
          },
        },
      },
    },
  });
}

export async function signInWithGoogle() {
  try {
    await signInWithRedirect({ provider: 'Google' });
  } catch (err: unknown) {
    if ((err as { name?: string })?.name !== 'UserAlreadyAuthenticatedException') throw err;
  }
}

export async function signInWithApple() {
  try {
    await signInWithRedirect({ provider: 'Apple' });
  } catch (err: unknown) {
    if ((err as { name?: string })?.name !== 'UserAlreadyAuthenticatedException') throw err;
  }
}

export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function getCurrentAuthUser() {
  try {
    return await getCurrentUser();
  } catch {
    return null;
  }
}

export { signOut };
